import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = '';

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [paymentInfo, setPaymentInfo] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId, 0);
    }
  }, [sessionId]);

  const pollPaymentStatus = async (sessionId, attempts) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/checkout/status/${sessionId}`,
        { withCredentials: true }
      );

      if (data.payment_status === 'paid') {
        setPaymentInfo(data);
        setStatus('success');
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      }

      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-[#F3F4F6] flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white p-8 border border-slate-200 rounded-md text-center">
        {status === 'checking' && (
          <>
            <Loader className="w-16 h-16 text-[#002FA7] mx-auto mb-4 animate-spin" />
            <h1 className="text-2xl font-black mb-2">Processing Payment</h1>
            <p className="text-slate-600">Please wait while we confirm your payment...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
            <h1 className="text-2xl font-black mb-2" data-testid="payment-success-message">Payment Successful!</h1>
            <p className="text-slate-600 mb-6">
              Your payment of ${(paymentInfo?.amount_total / 100).toFixed(2)} has been processed successfully.
            </p>
            <button
              onClick={() => navigate('/student')}
              className="px-6 py-3 bg-[#002FA7] text-white font-bold rounded-md hover:bg-[#001a5c]"
              data-testid="back-to-dashboard-button"
            >
              Back to Dashboard
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-[#E32636] rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white text-3xl font-bold">!</span>
            </div>
            <h1 className="text-2xl font-black mb-2">Error</h1>
            <p className="text-slate-600 mb-6">
              There was an error processing your payment. Please contact support.
            </p>
            <button
              onClick={() => navigate('/student')}
              className="px-6 py-3 bg-[#002FA7] text-white font-bold rounded-md hover:bg-[#001a5c]"
            >
              Back to Dashboard
            </button>
          </>
        )}

        {status === 'timeout' && (
          <>
            <div className="w-16 h-16 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white text-3xl font-bold">?</span>
            </div>
            <h1 className="text-2xl font-black mb-2">Payment Status Unknown</h1>
            <p className="text-slate-600 mb-6">
              We couldn't verify your payment. Please check your email for confirmation.
            </p>
            <button
              onClick={() => navigate('/student')}
              className="px-6 py-3 bg-[#002FA7] text-white font-bold rounded-md hover:bg-[#001a5c]"
            >
              Back to Dashboard
            </button>
          </>
        )}
      </div>
    </div>
  );
}
